import React, { useMemo, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useReactiveVar } from '@apollo/client';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';

import { Form } from '../../../common';
import { IEcpSearchProps, TSearchFormValues } from './ecp-search.models';
import {
  setAutocompletedValue,
  setAutocompletedSearch,
  setAutoValueSelected,
  setError,
  setEcpFormSubmitted,
  setGeoLocationReady,
  setNoLocationSearchReady,
} from '../../../../graphql/ecp-locator/ecp-locator.cache';
import { ErrorPlain, ErrorTypes } from '../../../common/error-plain';
import { EcpErrors } from '../../../../constants/ecp-locator/error-types';
import { useStyles } from './ecp-search.styles';
import { useSearchLocation } from '../../../../hooks/use-search-location';
import { YandexMapAutocomplete } from '../yandex-maps-autocomplete/yandex-maps-autocomplete';

export const EcpSearch = ({
  inputIcon,
  inputStyles,
  rootStyles,
  rootInputStyles,
  endAdornmentStyles,
  updateStores,
  isC2c = false,
}: IEcpSearchProps): JSX.Element => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { handleSubmit, setValue } = useForm<TSearchFormValues>();
  const fieldName = 'searchValue';
  const defaultValues = useMemo(() => ({ [fieldName]: '' }), []);
  const searchError = useReactiveVar(setError);
  const isAutocompletedSearch = useReactiveVar(setAutocompletedSearch);
  const autocompletedValue = useReactiveVar(setAutocompletedValue);
  const isGeoLocationReady = useReactiveVar(setGeoLocationReady);
  const isNoLocationSearchReady = useReactiveVar(setNoLocationSearchReady);
  const resultsRef = useRef<HTMLUListElement | null>(null);
  const classes = useStyles();
  const { updateLocationCoordinates } = useSearchLocation();
  const [autocompletedUri, setAutocompletedUri] = useState('');

  const handleSetInputValue = (searchValue: string | undefined) => {
    setValue(fieldName, searchValue!);
  };

  const handleAutoValueSelected = (isSelected: boolean) => {
    setAutoValueSelected(isSelected);
  };

  const handleAutocompletedSearch = (isSearch: boolean) => {
    setAutocompletedSearch(isSearch);
  };

  const handleAutocompletedValue = (searchValue: string) => {
    setAutocompletedValue(searchValue);
  };

  const handleAutocompletedUri = (uri: string) => {
    setAutocompletedUri(uri || '');
  };

  const handleSearch = async ({ searchValue }: TSearchFormValues) => {
    handleAutoValueSelected(true);
    const q = (autocompletedValue?.length ? autocompletedValue : searchValue)?.trim();

    if (!q) {
      setError(EcpErrors.noSearchValue);
      return;
    }

    try {
      const userLocation = await updateLocationCoordinates({ searchValue: q, uri: autocompletedUri });
      await updateStores({ userLocation });

      setEcpFormSubmitted(true);
      setAutocompletedUri('');

      setTimeout(() => {
        if (resultsRef.current) {
          resultsRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
          resultsRef.current.focus();
        }
      }, 300);
    } catch {
      setError(EcpErrors.errorValue);
      setAutocompletedValue('');
      setAutocompletedUri('');
    }
  };

  useEffect(() => {
    if (isAutocompletedSearch) {
      handleSearch({ searchValue: autocompletedValue });
      handleAutocompletedSearch(false);
    }
  }, [isAutocompletedSearch, autocompletedValue]);

  // Trigger search when geolocation coords are ready (set by withDefaultFiltersAndStoresList)
  useEffect(() => {
    if (!isGeoLocationReady) return;
    setGeoLocationReady(false);
    updateStores({}).then(() => {
      setEcpFormSubmitted(true);
    });
  }, [isGeoLocationReady]);

  // Trigger search when user enters without location permission or no location available
  useEffect(() => {
    if (!isNoLocationSearchReady) return;
    setNoLocationSearchReady(false);
    updateStores({}).then(() => {
      setEcpFormSubmitted(true);
    });
  }, [isNoLocationSearchReady]);

  return (
    <Form onSubmit={handleSubmit(handleSearch)} defaultValues={defaultValues}>
      <YandexMapAutocomplete
        fieldName={fieldName}
        onChildInputChange={handleSetInputValue}
        onAutocompletedSearch={handleAutocompletedSearch}
        onAutocompletedValue={handleAutocompletedValue}
        onAutocompletedUri={handleAutocompletedUri}
        onFormSubmitted={handleAutoValueSelected}
        isOpenPredictions
        variant={isMobile ? 'outlined' : 'standard'}
        startIcon={inputIcon}
        inputError={!!searchError}
        inputStyles={inputStyles}
        rootStyles={rootStyles}
        rootInputStyles={rootInputStyles}
        endAdornmentStyles={endAdornmentStyles}
        isC2c={isC2c}
      />
      {searchError === EcpErrors.errorValue && (
        <ErrorPlain content="ecp.error.errorValue" variant={ErrorTypes.error} />
      )}
      {searchError === EcpErrors.noSearchValue && (
        <ErrorPlain
          content="ecp.error.noSearchValue"
          variant={ErrorTypes.error}
          externalClass={classes.error}
        />
      )}
    </Form>
  );
};
